<template>
  <div class="relative">
    <input
      :id="elementId"
      class="peer absolute left-[-10000px] size-[0.01px] overflow-hidden opacity-0"
      :class="{ invalid: invalid }"
      :disabled="disabled"
      :name="name"
      type="checkbox"
      :value="model"
      :aria-invalid="invalid"
      @change="model = !model"
    />
    <label
      :for="elementId"
      class="flex items-start gap-4 peer-enabled:cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:fill-darkDisabled peer-disabled:opacity-50"
    >
      <span
        class="checkmark-area relative inline-flex items-center rounded-[2px]"
        :class="{ 'text-base': size === 'sm' }"
        data-test-id="checkbox-input"
      >
        <span class="block">
          <MaterialSymbolsCheckBoxOutlineBlank
            v-if="!model"
            :class="checkmarkClasses"
          />
          <MaterialSymbolsCheckBoxRounded v-else :class="checkmarkClasses" />
        </span>
      </span>

      <span
        v-if="$slots.default"
        class="block w-full leading-tight"
        :class="{ 'text-danger': invalid }"
        data-test-id="checkbox-label"
      >
        <slot />
      </span>
    </label>
  </div>
</template>

<script lang="ts" setup>
import { uniqueId } from 'lodash-es';
import { computed } from 'vue';

interface Props {
  id?: string;
  name?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'md' | 'sm';
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  name: undefined,
  disabled: false,
  invalid: false,
  size: 'md',
});

const model = defineModel<boolean>({ default: false });

const fallbackId = uniqueId('checkbox');

const elementId = computed(() => props.id || fallbackId);
const checkmarkClasses = computed(() => {
  let color: string;
  if (props.invalid) {
    color = 'text-danger';
  } else if (props.disabled) {
    color = 'text-darkDisabled';
  } else {
    color = 'text-primary';
  }
  return `${color} scale-125`;
});
</script>

<style scoped lang="scss">
label > .checkmark-area {
  input:valid:focus-visible + & {
    @apply outline outline-2 outline-offset-2 outline-text/50;
  }

  input:invalid:focus-visible + &,
  input.invalid:focus-visible + & {
    @apply text-danger outline outline-2 outline-offset-2 outline-danger/50;
  }
}
</style>
